import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "hiddenField", "selectedList", "selectedTemplate"]
  static values = { 
    url: String, 
    multiple: Boolean,
    quoteId: String,
    quoteType: String
  }

  connect() {
    this.searchTimeout = null
    this.selectedSecurities = new Map()
    
    // Add click outside handler
    this.clickOutsideHandler = this.clickOutside.bind(this)
    document.addEventListener("click", this.clickOutsideHandler)
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    document.removeEventListener("click", this.clickOutsideHandler)
  }

  search(event) {
    const query = event.target.value.trim()
    
    // Clear timeout if exists
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Hide results if query is empty
    if (query.length < 2) {
      this.hideResults()
      return
    }

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const params = new URLSearchParams({ q: query })
      
      // Add quote context if available
      if (this.hasQuoteIdValue && this.quoteIdValue) {
        params.append('quote_id', this.quoteIdValue)
      }
      if (this.hasQuoteTypeValue && this.quoteTypeValue) {
        params.append('quote_type', this.quoteTypeValue)
      }
      
      const fullUrl = `${this.urlValue}?${params}`
      
      // Use Turbo to fetch HTML
      const turboFrame = this.resultsTarget.querySelector('turbo-frame')
      if (turboFrame) {
        turboFrame.src = fullUrl
        this.showResults()
      }
    } catch (error) {
      console.error("Security search error:", error)
      this.hideResults()
    }
  }

  selectSecurity(event) {
    const securityData = event.currentTarget.dataset.securityData
    if (!securityData) return
    
    const security = JSON.parse(securityData)
    
    if (this.multipleValue) {
      // Multiple selection mode
      if (!this.selectedSecurities.has(security.id)) {
        this.selectedSecurities.set(security.id, security)
        this.addSelectedSecurity(security)
      }
      this.inputTarget.value = ""
    } else {
      // Single selection mode
      this.hiddenFieldTarget.value = security.id
      this.inputTarget.value = security.display_name || `${security.issuer_name} - ${security.isin}`
    }
    
    this.hideResults()
    
    // Dispatch custom event
    this.dispatch("selected", { detail: security })
  }

  addSelectedSecurity(security) {
    if (!this.hasSelectedTemplateTarget) {
      console.error('Selected template not found')
      return
    }

    // Check if security already exists in line items by ID
    if (this.securityExistsInLineItems(security.id)) {
      return
    }

    // Clone the template
    const template = this.selectedTemplateTarget.content.cloneNode(true)
    
    // Replace placeholders with actual values
    const html = template.querySelector('div').outerHTML
      .replace(/{{id}}/g, security.id)
      .replace(/{{issuer_name}}/g, this.escapeHtml(security.issuer_name))
      .replace(/{{isin}}/g, this.escapeHtml(security.isin))
      .replace(/{{maturity_date}}/g, this.escapeHtml(security.maturity_date || ''))
    
    // Create a temporary container to parse the HTML
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    const element = tempDiv.firstChild
    
    this.selectedListTarget.appendChild(element)
  }

  securityExistsInLineItems(securityId) {
    const lineItemsContainer = document.querySelector('[data-oms--quote-securities-target="lineItems"]')
    if (!lineItemsContainer) {
      return false
    }

    const existingInputs = lineItemsContainer.querySelectorAll('input[name*="[security_id]"]')
    return Array.from(existingInputs).some(input => {
      if (input.value != securityId) return false
      
      const lineItem = input.closest('.quote-line-item')
      if (!lineItem) return false
      
      if (lineItem.style.display === 'none') return false
      
      const destroyField = lineItem.querySelector('input[name*="_destroy"]')
      if (destroyField && destroyField.value === "1") return false
      
      return true
    })
  }


  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  removeSecurity(event) {
    event.preventDefault()
    const securityId = event.currentTarget.dataset.securityId
    
    if (this.selectedSecurities.has(parseInt(securityId))) {
      this.selectedSecurities.delete(parseInt(securityId))
      
      // Remove from DOM
      const element = this.selectedListTarget.querySelector(`[data-security-id="${securityId}"]`).closest('[data-security-id]')
      if (element) {
        element.remove()
      }
      
      // Dispatch custom event
      this.dispatch("removed", { detail: { id: securityId } })
    }
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
  }

  // Handle clicking outside
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }
}